import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * PAINEL LATERAL DIREITO (dock) — canal entre o layout (Shell), o hospedeiro
 * do painel (DayAgendaDrawer) e as "vistas" que moram nele.
 *
 * O painel é `position: fixed` na borda direita — FORA do fluxo. Pra ele
 * ENCAIXAR ao lado (sem cobrir o sistema), o hospedeiro publica aqui a
 * largura que está ocupando (trilho + painel aberto); o Shell lê e aplica
 * como `padding-right` na coluna de conteúdo, que se encolhe e cede o
 * espaço exato. "0px" = nada reservado (hospedeiro ausente, ex.: /agenda).
 *
 *  * Só UMA vista fica aberta por vez (agenda · chat · resumo · optotipos ·
 * paciente) —
 * é isso que impede um painel de abrir por cima do outro. As vistas que não
 * são a agenda (chat, resumo clínico, optotipos) se REGISTRAM aqui quando
 * montam (ganham um ícone no trilho) e, quando ativas, renderizam o próprio
 * conteúdo via portal no `slotEl` que o hospedeiro expõe dentro do painel.
 */
export type DockView = 'agenda' | 'chat' | 'resumo' | 'optotipos' | 'paciente';

export interface DockViewMeta {
  /** Rótulo do ícone no trilho (tooltip). */
  label: string;
  /** Contador exibido sobre o ícone (ex.: mensagens não lidas). */
  badge?: number;
}

type AgendaDockValue = {
  /** Largura CSS reservada à direita (trilho + painel). Publicada pelo hospedeiro. */
  dockWidth: string;
  setDockWidth: (w: string) => void;

  /** Hospedeiro montado nesta rota (fora da /agenda e com clínica). Sem ele,
   *  as vistas voltam ao comportamento autônomo (gaveta própria). */
  hostMounted: boolean;
  setHostMounted: (v: boolean) => void;

  /** Painel aberto? E em qual vista. */
  open: boolean;
  view: DockView;
  openView: (v: DockView) => void;
  closeDock: () => void;
  /** Abre a vista; se ela já está aberta, fecha o painel. */
  toggleView: (v: DockView) => void;

  /** Vistas disponíveis no trilho (além da agenda, que é fixa). */
  views: Partial<Record<DockView, DockViewMeta>>;
  registerView: (v: DockView, meta: DockViewMeta) => void;
  unregisterView: (v: DockView) => void;
  setViewBadge: (v: DockView, badge: number) => void;

  /** Alvo do portal das vistas não-agenda (div dentro do painel aberto). */
  slotEl: HTMLElement | null;
  setSlotEl: (el: HTMLElement | null) => void;
};

const AgendaDockContext = createContext<AgendaDockValue | null>(null);

/** Chave da preferência "manter sempre aberta" da agenda — lida aqui pra o
 *  painel já NASCER aberto na agenda quando o médico pediu. */
export const AGENDA_ALWAYS_OPEN_STORAGE_KEY = 'dratento-agenda-always-open';

export const AgendaDockProvider = ({ children }: { children: ReactNode }) => {
  const [dockWidth, setDockWidth] = useState('0px');
  const [hostMounted, setHostMounted] = useState(false);
  // Aberto/fechado + vista ativa num estado só, pra alternar de forma atômica.
  const [panel, setPanel] = useState<{ open: boolean; view: DockView }>(() => {
    let open = false;
    // No celular (<sm) o painel é tela cheia: "manter aberta" não vale lá.
    try { open = localStorage.getItem(AGENDA_ALWAYS_OPEN_STORAGE_KEY) === '1' && window.innerWidth >= 640; } catch { /* sem storage */ }
    return { open, view: 'agenda' };
  });
  const [views, setViews] = useState<Partial<Record<DockView, DockViewMeta>>>({});
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);

  const openView = useCallback((v: DockView) => setPanel({ open: true, view: v }), []);
  const closeDock = useCallback(() => setPanel((p) => (p.open ? { ...p, open: false } : p)), []);
  const toggleView = useCallback(
    (v: DockView) => setPanel((p) => ({ open: !(p.open && p.view === v), view: v })),
    [],
  );

  const registerView = useCallback((v: DockView, meta: DockViewMeta) => {
    setViews((prev) => {
      const cur = prev[v];
      if (cur && cur.label === meta.label && cur.badge === meta.badge) return prev;
      return { ...prev, [v]: { ...cur, ...meta } };
    });
  }, []);
  const unregisterView = useCallback((v: DockView) => {
    setViews((prev) => {
      if (!(v in prev)) return prev;
      const next = { ...prev };
      delete next[v];
      return next;
    });
    // Se a vista que sumiu era a aberta (ex.: saiu da ficha com o Resumo
    // aberto), o painel volta pra agenda em vez de ficar vazio.
    setPanel((p) => (p.view === v ? { ...p, view: 'agenda' } : p));
  }, []);
  const setViewBadge = useCallback((v: DockView, badge: number) => {
    setViews((prev) => {
      const cur = prev[v];
      if (!cur || cur.badge === badge) return prev;
      return { ...prev, [v]: { ...cur, badge } };
    });
  }, []);

  const value = useMemo<AgendaDockValue>(
    () => ({
      dockWidth, setDockWidth,
      hostMounted, setHostMounted,
      open: panel.open, view: panel.view, openView, closeDock, toggleView,
      views, registerView, unregisterView, setViewBadge,
      slotEl, setSlotEl,
    }),
    [dockWidth, hostMounted, panel, openView, closeDock, toggleView, views, registerView, unregisterView, setViewBadge, slotEl],
  );
  return <AgendaDockContext.Provider value={value}>{children}</AgendaDockContext.Provider>;
};

const noop = () => {};

/** Sem provider (ex.: testes isolados), vira um no-op — o hospedeiro nunca
 *  está montado, então cada vista segue autônoma e o layout não reserva nada. */
export const useAgendaDock = (): AgendaDockValue => {
  const ctx = useContext(AgendaDockContext);
  return (
    ctx ?? {
      dockWidth: '0px', setDockWidth: noop,
      hostMounted: false, setHostMounted: noop,
      open: false, view: 'agenda', openView: noop, closeDock: noop, toggleView: noop,
      views: {}, registerView: noop, unregisterView: noop, setViewBadge: noop,
      slotEl: null, setSlotEl: noop,
    }
  );
};
